import { z } from 'zod';

/**
 * The state of the provider settings screen, as the URL carries it
 * (`rules/lists-and-filters.mdc` §1 — the same reasoning, for a screen whose one piece of state is
 * «which provider is open for editing»).
 *
 * **The editor is a section of the page addressed by a parameter, not a modal**, and that follows
 * the product's own rule rather than taste: a form that needs more than thirty seconds of attention
 * is a page or a drawer with the state in the URL, never a modal
 * (`rules/design-system.mdc` §16). Configuring a provider is ten fields, one of which sends the
 * organization's documents out of the building — it is not a thirty-second decision. The parameter
 * also means «покажи, что не так с этим провайдером» is a link.
 *
 * **`.catch` rather than `.default`**, like every other search schema here: a present value that
 * means nothing — a provider id that has since been deleted, a word somebody typed — must fall back
 * rather than fail validation, because a failed `validateSearch` replaces the screen with the error
 * boundary and, with `replace: true` on every write, leaves the broken address in the bar.
 */

/** What `edit` means when it is not an id: «open a blank form». */
export const AI_PROVIDER_NEW = 'new';

/** Long enough for a UUID and for the sentinel above; anything longer is not either of them. */
const MAX_EDIT = 64;

export const aiProviderSearchSchema = z.object({
  /**
   * `new` for a provider being created, an id for one being edited, absent for neither.
   *
   * An id that matches no provider resolves to «no editor open» rather than to an error: the row may
   * have been deleted in another tab, and a stale link is not a fault worth an error screen.
   */
  edit: z.string().trim().min(1).max(MAX_EDIT).optional().catch(undefined),
});

export type AiProviderSearch = z.infer<typeof aiProviderSearchSchema>;
